'use client';

import Link from 'next/link';
import { ChevronLeft } from 'lucide-react';
import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { useIsMobile } from '@/hooks/use-mobile';
import { cleanCode } from './code-input';

/**
 * The athlete hub's small parts, drawn from the Athlete Hub handoff in the v8
 * tokens. Hairline rows rather than cards, sentence case throughout, Geist
 * Mono for figures, one teal fill per view, amber only for "needs you".
 *
 * Shared by every hub screen, the emailed ticket page (which wears the same
 * look in the organiser's colours) and the public photo pages. One module, so
 * the screens cannot drift apart.
 */

export const hubText = {
  h1: { margin: 0, fontSize: 23, fontWeight: 600, letterSpacing: '-0.028em', color: 'var(--fvd-ink)', lineHeight: 1.2, textWrap: 'balance' } as CSSProperties,
  sub: { fontSize: 13.5, color: 'var(--fvd-ink-secondary)', marginTop: 7, lineHeight: 1.5 } as CSSProperties,
  h2: { margin: 0, fontSize: 14, fontWeight: 600, letterSpacing: '-0.01em', color: 'var(--fvd-ink)' } as CSSProperties,
  label: { margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--fvd-ink-muted)' } as CSSProperties,
  small: { fontSize: 11.5, color: 'var(--fvd-ink-muted)' } as CSSProperties,
  body: { fontSize: 13, color: 'var(--fvd-ink-secondary)', lineHeight: 1.6 } as CSSProperties,
  mono: { fontFamily: 'var(--fvd-font-mono)', fontVariantNumeric: 'tabular-nums' } as CSSProperties,
};

/** A muted section label with an optional right-hand aside (a count, a link). */
export function SectionLabel({ children, aside, style }: { children: ReactNode; aside?: ReactNode; style?: CSSProperties }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12, ...style }}>
      <h2 style={hubText.label}>{children}</h2>
      <span style={{ flex: 1 }} />
      {aside}
    </div>
  );
}

/** A section heading in ink, for sections that are the subject of the page. */
export function SectionHeading({ children, aside, style }: { children: ReactNode; aside?: ReactNode; style?: CSSProperties }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4, ...style }}>
      <h2 style={hubText.h2}>{children}</h2>
      <span style={{ flex: 1 }} />
      {aside}
    </div>
  );
}

export type PillTone = 'teal' | 'amber' | 'danger' | 'neutral';

const PILL: Record<PillTone, { bg: string; fg: string }> = {
  teal: { bg: 'var(--fvd-teal-tint)', fg: 'var(--fvd-teal-text)' },
  amber: { bg: 'var(--fvd-amber-tint)', fg: 'var(--fvd-amber-text)' },
  danger: { bg: 'var(--fvd-danger-tint)', fg: 'var(--fvd-danger)' },
  neutral: { bg: 'var(--fvd-neutral-chip)', fg: 'var(--fvd-ink-secondary)' },
};

export function HubPill({ tone = 'neutral', children, icon, style }: { tone?: PillTone; children: ReactNode; icon?: ReactNode; style?: CSSProperties }) {
  const c = PILL[tone];
  return (
    <span
      style={{
        flex: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: 22,
        padding: '0 8px',
        borderRadius: 'var(--fvd-radius-chip)',
        background: c.bg,
        color: c.fg,
        fontSize: 11.5,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {icon}
      {children}
    </span>
  );
}

/** Label on the left, value on the right, hairline under. */
export function KeyRow({ label, value, mono = true, strong }: { label: ReactNode; value: ReactNode; mono?: boolean; strong?: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 12,
        padding: '10px 0',
        borderBottom: '1px solid var(--fvd-hairline-light)',
      }}
    >
      <span style={{ fontSize: 13, color: strong ? 'var(--fvd-ink)' : 'var(--fvd-ink-secondary)', fontWeight: strong ? 500 : 400 }}>{label}</span>
      <span style={{ fontSize: 13.5, color: 'var(--fvd-ink)', textAlign: 'right', minWidth: 0, ...(mono ? hubText.mono : {}) }}>{value}</span>
    </div>
  );
}

export type HubButtonVariant = 'primary' | 'secondary' | 'small' | 'amber' | 'dark';

/** The hub's buttons: 40px with a mouse, 44px on a phone. */
export function hubButtonStyle(variant: HubButtonVariant, isMobile: boolean, extra?: CSSProperties): CSSProperties {
  const tall = variant === 'small' || variant === 'amber' ? 32 : 40;
  const base: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    height: isMobile ? 44 : tall,
    padding: variant === 'small' || variant === 'amber' ? '0 13px' : '0 16px',
    borderRadius: variant === 'small' || variant === 'amber' ? 8 : 10,
    fontFamily: 'inherit',
    fontSize: variant === 'small' || variant === 'amber' ? 12.5 : 13.5,
    fontWeight: variant === 'primary' ? 600 : 500,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    textDecoration: 'none',
    lineHeight: 1,
  };
  if (variant === 'primary') {
    return { ...base, border: 'none', background: 'var(--fvd-teal-solid)', color: 'var(--fvd-teal-solid-on)', ...extra };
  }
  if (variant === 'dark') {
    return { ...base, border: '1px solid var(--fvd-hairline-strong)', background: '#000000', color: '#FFFFFF', ...extra };
  }
  if (variant === 'amber') {
    return { ...base, border: '1px solid var(--fvd-amber)', background: 'transparent', color: 'var(--fvd-amber-text)', ...extra };
  }
  return { ...base, border: '1px solid var(--fvd-border-control)', background: 'transparent', color: 'var(--fvd-ink)', ...extra };
}

export function HubButton({
  children,
  onClick,
  variant = 'secondary',
  disabled,
  type = 'button',
  style,
  ariaLabel,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: HubButtonVariant;
  disabled?: boolean;
  type?: 'button' | 'submit';
  style?: CSSProperties;
  ariaLabel?: string;
}) {
  const isMobile = useIsMobile();
  return (
    <button
      type={type}
      aria-label={ariaLabel}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={disabled ? undefined : variant === 'primary' || variant === 'dark' ? 'fvd-hoverable-solid' : 'fvd-hoverable'}
      style={hubButtonStyle(variant, isMobile, { opacity: disabled ? 0.5 : 1, cursor: disabled ? 'default' : 'pointer', ...style })}
    >
      {children}
    </button>
  );
}

export function HubButtonLink({
  href,
  children,
  variant = 'secondary',
  style,
  external,
}: {
  href: string;
  children: ReactNode;
  variant?: HubButtonVariant;
  style?: CSSProperties;
  external?: boolean;
}) {
  const isMobile = useIsMobile();
  const s = hubButtonStyle(variant, isMobile, style);
  const cls = variant === 'primary' || variant === 'dark' ? 'fvd-hoverable-solid' : 'fvd-hoverable';
  if (/^(mailto|tel):/.test(href)) {
    return (
      <a href={href} className={cls} style={s}>
        {children}
      </a>
    );
  }
  if (external) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={cls} style={s}>
        {children}
      </a>
    );
  }
  return (
    <Link href={href} className={cls} style={s}>
      {children}
    </Link>
  );
}

/** A text link in the accent, with a phone-sized hit area. */
export function HubTextLink({ href, children, external, onClick }: { href?: string; children: ReactNode; external?: boolean; onClick?: () => void }) {
  const isMobile = useIsMobile();
  const s: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: isMobile ? 44 : undefined,
    fontSize: 12.5,
    fontWeight: 500,
    color: 'var(--fvd-teal-text)',
    textDecoration: 'none',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    fontFamily: 'inherit',
  };
  if (!href) {
    return (
      <button type="button" onClick={onClick} style={s}>
        {children}
      </button>
    );
  }
  if (external) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" style={s}>
        {children}
      </a>
    );
  }
  return (
    <Link href={href} style={s}>
      {children}
    </Link>
  );
}

/** Skeleton block in the v8 neutral, so loading matches the page in both themes. */
export function HubSkeleton({ height = 14, width = '100%', radius = 8, style }: { height?: number; width?: number | string; radius?: number; style?: CSSProperties }) {
  return <Skeleton style={{ height, width, borderRadius: radius, background: 'var(--fvd-neutral-chip)', ...style }} />;
}

/** A grey tile standing in for a missing cover image. */
export function ImagePlaceholder({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3.5" y="5" width="17" height="14" rx="2.5" />
      <path d="m5 16.5 4-4 3.5 3.5 3-2.5 3.5 3" />
      <circle cx="9" cy="9.5" r="1.3" />
    </svg>
  );
}

/** An event cover that falls back to the placeholder tile instead of a broken image. */
export function CoverThumb({ src, alt, size = 56, radius = 10 }: { src?: string | null; alt: string; size?: number; radius?: number }) {
  // A server-rendered image can fail before React attaches onError, which
  // left a broken-image icon; check it once it has mounted too.
  const img = useRef<HTMLImageElement>(null);
  useEffect(() => {
    const el = img.current;
    if (el && el.complete && el.naturalWidth === 0) el.style.visibility = 'hidden';
  }, [src]);
  return (
    <span
      style={{
        flex: 'none',
        width: size,
        height: size,
        borderRadius: radius,
        background: 'var(--fvd-neutral-chip)',
        color: 'var(--fvd-ink-disabled)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <ImagePlaceholder />
      {src && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          ref={img}
          src={src}
          alt={alt}
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}
    </span>
  );
}

/* ---- Page structure: title, hairline sections and rows, figures ---- */

/**
 * The athlete hub's page pieces, drawn from the Athlete Hub handoff
 * (athlete-04 stats, -05 discover, -06 profile): a 23px title with one line
 * under it, sections divided by a hairline rather than boxed in cards, and
 * rows divided by a lighter hairline.
 *
 * Colours are the v8 tokens (--fvd-*) only, so light and dark both come from
 * globals.css, and the Hyrox brand preview needs nothing here: the hub shell
 * re-points those tokens at the Hyrox palette (components/hub/hub-skin.ts).
 *
 * The few rules a style attribute cannot express (hover, placeholder colour,
 * the shared phone field's border) live in a runtime style tag rather than
 * globals.css, because Tailwind v4 drops user-authored class rules it cannot
 * see used in markup, which has removed rules from that file before.
 */
const HUB_PAGE_CSS = `
.hub-page { color: var(--fvd-ink); font-family: var(--fvd-font-body); font-variant-numeric: tabular-nums; }
.hub-page a.hub-link { color: var(--fvd-teal-text); text-decoration: none; }
.hub-page a.hub-link:hover { text-decoration: underline; }
.hub-page .hub-row-link { transition: background 120ms ease; }
.hub-page .hub-row-link:hover { background: var(--fvd-row-hover); }
.hub-page .fv-phone-input.PhoneInput { border: 1px solid var(--fvd-border-control) !important; }
.hub-page .fv-phone-input:focus-within { outline: 2px solid var(--fvd-teal) !important; outline-offset: -1px; border-radius: var(--fvd-radius); }
.hub-page .fv-phone-input .PhoneInputInput::placeholder { color: var(--fvd-ink-muted); }
.hub-page input::placeholder, .hub-page textarea::placeholder { color: var(--fvd-ink-muted); }
.hub-page input:focus-visible, .hub-page textarea:focus-visible { outline: 2px solid var(--fvd-teal); outline-offset: -1px; }
`;

/** The root of a hub page: base font, ink and the few rules styles cannot express. */
export function HubPage({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div className="hub-page" style={{ width: '100%', minWidth: 0, paddingBottom: 24, ...style }}>
      <style>{HUB_PAGE_CSS}</style>
      {children}
    </div>
  );
}

/** Page title and the one line under it. `aside` sits on the right (an Edit button, a toggle). */
export function HubTitle({
  title,
  subtitle,
  aside,
  back,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  aside?: ReactNode;
  /** A quiet link above the title, back to where this page was opened from. */
  back?: { href: string; label: string };
}) {
  return (
    <div>
      {back && (
        <Link
          href={back.href}
          className="hub-link"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            minHeight: 36,
            marginBottom: 6,
            marginLeft: -4,
            fontSize: 13,
            color: 'var(--fvd-ink-secondary)',
            textDecoration: 'none',
          }}
        >
          <ChevronLeft size={16} strokeWidth={1.8} aria-hidden />
          {back.label}
        </Link>
      )}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: 23, fontWeight: 600, letterSpacing: '-0.028em', lineHeight: 1.25, color: 'var(--fvd-ink)' }}>
            {title}
          </h1>
          {subtitle && (
            <div style={{ fontSize: 13.5, color: 'var(--fvd-ink-secondary)', marginTop: 7, lineHeight: 1.5 }}>{subtitle}</div>
          )}
        </div>
        {aside && <div style={{ flex: 'none' }}>{aside}</div>}
      </div>
    </div>
  );
}

/**
 * A section of a page. The first one sits under the title with no rule; every
 * one after it is separated by a hairline across the column, as the handoff
 * draws it, rather than boxed in a card.
 */
export function HubSection({
  title,
  note,
  aside,
  first,
  children,
  style,
}: {
  title?: ReactNode;
  note?: ReactNode;
  aside?: ReactNode;
  first?: boolean;
  children: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <section
      style={{
        marginTop: first ? 22 : 30,
        paddingTop: first ? 0 : 24,
        borderTop: first ? 'none' : '1px solid var(--fvd-hairline)',
        ...style,
      }}
    >
      {(title || aside) && (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: note ? 4 : 6 }}>
          {title && (
            <h2 style={{ margin: 0, fontSize: 14, fontWeight: 600, letterSpacing: '-0.01em', color: 'var(--fvd-ink)' }}>{title}</h2>
          )}
          <span style={{ flex: 1 }} />
          {aside}
        </div>
      )}
      {note && <div style={{ fontSize: 12.5, color: 'var(--fvd-ink-muted)', marginBottom: 12, lineHeight: 1.55 }}>{note}</div>}
      {children}
    </section>
  );
}

/** One hairline row: a label and a note on the left, anything on the right. */
export function HubRow({
  label,
  note,
  noteTone = 'muted',
  right,
  leading,
  last,
}: {
  label: ReactNode;
  note?: ReactNode;
  noteTone?: 'muted' | 'amber' | 'teal';
  right?: ReactNode;
  leading?: ReactNode;
  last?: boolean;
}) {
  const noteColour =
    noteTone === 'amber' ? 'var(--fvd-amber-text)' : noteTone === 'teal' ? 'var(--fvd-teal-text)' : 'var(--fvd-ink-muted)';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '13px 0',
        minHeight: 44,
        borderBottom: last ? 'none' : '1px solid var(--fvd-hairline-light)',
      }}
    >
      {leading}
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 13.5, fontWeight: 500, color: 'var(--fvd-ink)' }}>{label}</span>
        {note && <span style={{ display: 'block', fontSize: 12.5, color: noteColour, marginTop: 2, lineHeight: 1.5 }}>{note}</span>}
      </span>
      {right !== undefined && <span style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 10 }}>{right}</span>}
    </div>
  );
}

/** A read-only figure in the four-up row (athlete-04): label above, mono value below. */
export function HubFigures({ items }: { items: Array<{ key: string; label: string; value: ReactNode }> }) {
  return (
    <div
      style={{
        display: 'grid',
        // auto-fit rather than a breakpoint: four across at full width, two on a
        // phone, with no media query for an inline style to lose to.
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: 8,
      }}
    >
      {items.map((it) => (
        <div
          key={it.key}
          style={{ padding: '11px 13px', border: '1px solid var(--fvd-hairline-strong)', borderRadius: 9, minWidth: 0 }}
        >
          <div style={{ fontSize: 12, color: 'var(--fvd-ink-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {it.label}
          </div>
          <div style={{ fontFamily: 'var(--fvd-font-mono)', fontSize: 16, marginTop: 6, color: 'var(--fvd-ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {it.value}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Round avatar: the photo when there is one, otherwise initials on the accent.
 * The initials sit underneath the photo, so a picture that fails to load (a
 * deleted upload, a dead link) shows them rather than a broken-image icon.
 */
export function HubAvatar({
  url,
  initials,
  size = 56,
  tone = 'solid',
  ring,
}: {
  url?: string | null;
  initials: string;
  size?: number;
  /** solid: the accent fill (the athlete's own face); tint: a pale chip (other people). */
  tone?: 'solid' | 'tint';
  /** A canvas-coloured ring, for faces that overlap. */
  ring?: boolean;
}) {
  const img = useRef<HTMLImageElement>(null);
  useEffect(() => {
    // A server-rendered image can fail before React attaches onError.
    const el = img.current;
    if (el && el.complete && el.naturalWidth === 0) el.style.visibility = 'hidden';
  }, [url]);
  return (
    <span
      aria-hidden
      style={{
        flex: 'none',
        position: 'relative',
        width: size,
        height: size,
        boxSizing: ring ? 'border-box' : undefined,
        border: ring ? '1.5px solid var(--fvd-canvas)' : undefined,
        borderRadius: '50%',
        overflow: 'hidden',
        background: tone === 'tint' ? 'var(--fvd-teal-tint)' : 'var(--fvd-teal-solid, var(--fvd-teal))',
        color: tone === 'tint' ? 'var(--fvd-teal-text)' : 'var(--fvd-teal-solid-on, var(--fvd-teal-on))',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: Math.max(8, Math.round(size * (size <= 26 ? 0.4 : 0.33))),
        fontWeight: 600,
        lineHeight: 1,
      }}
    >
      {initials}
      {url && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          ref={img}
          src={url}
          alt=""
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      )}
    </span>
  );
}

/** Skeleton for a title and its subtitle line. */
export function HubTitleBones() {
  return (
    <div>
      <HubSkeleton width={180} height={26} />
      <HubSkeleton width={260} height={14} style={{ marginTop: 10 }} />
    </div>
  );
}

/** Skeleton hairline rows, shaped like HubRow. */
export function HubRowBones({ rows = 4, leading }: { rows?: number; leading?: number }) {
  return (
    <div>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 0', borderBottom: '1px solid var(--fvd-hairline-light)' }}>
          {leading ? <HubSkeleton width={leading} height={leading} radius={10} /> : null}
          <div style={{ flex: 1 }}>
            <HubSkeleton width="55%" height={14} />
            <HubSkeleton width="35%" height={12} style={{ marginTop: 7 }} />
          </div>
          <HubSkeleton width={56} height={14} />
        </div>
      ))}
    </div>
  );
}

/** An inline message: red for a failure, teal for a success. Never a browser dialog. */
export function HubNotice({ tone, children }: { tone: 'danger' | 'success' | 'muted'; children: ReactNode }) {
  const colour = tone === 'danger' ? 'var(--fvd-danger)' : tone === 'success' ? 'var(--fvd-teal-text)' : 'var(--fvd-ink-secondary)';
  return (
    <p role={tone === 'danger' ? 'alert' : 'status'} style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: colour }}>
      {children}
    </p>
  );
}

/**
 * The six-digit code field, drawn as the sign-in page draws it: one wide mono
 * input rather than six boxes, so a pasted or autofilled code ("123 456")
 * lands whole. Digits only, at most six; the caller acts on the sixth.
 * readOnly rather than disabled while checking, so the phone keyboard stays up.
 */
export function HubCodeInput({
  value,
  onChange,
  busy,
  invalid,
  autoFocus,
  ariaLabel = 'Six-digit code',
}: {
  value: string;
  onChange: (code: string) => void;
  busy?: boolean;
  invalid?: boolean;
  autoFocus?: boolean;
  ariaLabel?: string;
}) {
  return (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="one-time-code"
      pattern="[0-9]*"
      enterKeyHint="go"
      autoFocus={autoFocus}
      value={value}
      onChange={(e) => onChange(cleanCode(e.target.value))}
      placeholder="000000"
      aria-label={ariaLabel}
      aria-invalid={invalid || undefined}
      readOnly={busy}
      style={{
        width: '100%',
        maxWidth: 280,
        boxSizing: 'border-box',
        height: 52,
        // The extra left padding balances the trailing letter-spacing so the
        // digits sit dead centre.
        padding: '0 14px 0 calc(14px + .42em)',
        borderRadius: 10,
        border: `1px solid ${invalid ? 'var(--fvd-danger)' : 'var(--fvd-border-control)'}`,
        background: 'var(--fvd-canvas)',
        color: 'var(--fvd-ink)',
        fontFamily: 'var(--fvd-font-mono)',
        fontSize: 22,
        letterSpacing: '.42em',
        textAlign: 'center',
        opacity: busy ? 0.7 : 1,
      }}
    />
  );
}

/** The activity type as a small chip, in sentence case. */
export function KindChip({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'teal' }) {
  return (
    <span
      style={{
        flex: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        height: 19,
        padding: '0 7px',
        borderRadius: 5,
        background: tone === 'teal' ? 'var(--fvd-teal-tint)' : 'var(--fvd-neutral-chip)',
        color: tone === 'teal' ? 'var(--fvd-teal-text)' : 'var(--fvd-ink-secondary)',
        fontSize: 11,
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

/**
 * "hyrox_simulation" or "HYROX" to "Hyrox simulation". Distances keep their
 * capital ("10K", "5K"): lower-casing them read "Best 10k time".
 */
export function sentenceCase(value: string | null | undefined): string {
  if (!value) return '';
  const s = String(value).replace(/[_-]+/g, ' ').trim().toLowerCase().replace(/\b(\d+(?:\.\d+)?)k\b/g, '$1K');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** A segmented two-or-more way switch, as the handoff's Upcoming / Past. */
export function HubSegments<K extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: Array<{ key: K; label: string; count?: number }>;
  value: K;
  onChange: (k: K) => void;
  label: string;
}) {
  const isMobile = useIsMobile();
  return (
    <div
      role="tablist"
      aria-label={label}
      style={{ display: 'flex', alignItems: 'center', gap: 2, padding: 2, border: '1px solid var(--fvd-hairline-strong)', borderRadius: 8, width: 'fit-content', maxWidth: '100%' }}
    >
      {options.map((o) => {
        const on = o.key === value;
        return (
          <button
            key={o.key}
            role="tab"
            aria-selected={on}
            type="button"
            onClick={() => onChange(o.key)}
            className={on ? undefined : 'fvd-hoverable'}
            style={{
              height: isMobile ? 44 : 27,
              padding: '0 12px',
              border: 'none',
              borderRadius: 6,
              fontFamily: 'inherit',
              fontSize: 12.5,
              cursor: 'pointer',
              background: on ? 'var(--fvd-selected-fill)' : 'transparent',
              color: on ? 'var(--fvd-ink)' : 'var(--fvd-ink-muted)',
              fontWeight: on ? 600 : 400,
              whiteSpace: 'nowrap',
            }}
          >
            {o.label}
            {typeof o.count === 'number' && <span style={{ ...hubText.mono, marginLeft: 6, fontWeight: 400 }}>{o.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

/* ---- Icons, from the handoff (24px grid, 1.75 stroke) ---- */

function Svg({ size = 18, children, stroke = 1.75 }: { size?: number; children: ReactNode; stroke?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  );
}

export const HubIcons = {
  Home: (p: { size?: number }) => <Svg {...p}><path d="M4 10.5 12 4l8 6.5" /><path d="M6 10v9.5h12V10" /></Svg>,
  Races: (p: { size?: number }) => <Svg {...p}><circle cx="12" cy="9" r="4.5" /><path d="M9.5 13.2 8 20.5l4-2 4 2-1.5-7.3" /></Svg>,
  Discover: (p: { size?: number }) => <Svg {...p}><circle cx="12" cy="12" r="8.5" /><path d="m14.5 9.5-1.6 4-4 1.6 1.6-4Z" /></Svg>,
  Stats: (p: { size?: number }) => <Svg {...p}><path d="M4.5 19.5V13M9.5 19.5V8M14.5 19.5v-4.5M19.5 19.5V5" /></Svg>,
  Profile: (p: { size?: number }) => <Svg {...p}><circle cx="12" cy="8.5" r="3.6" /><path d="M5 19.5a7 7 0 0 1 14 0" /></Svg>,
  Pen: (p: { size?: number }) => <Svg {...p}><path d="M4 20h4l10-10a2.5 2.5 0 0 0-3.5-3.5L4.5 16.5Z" /></Svg>,
  Transfer: (p: { size?: number }) => <Svg {...p}><path d="M4 8.5h13l-3-3M20 15.5H7l3 3" /></Svg>,
  Refund: (p: { size?: number }) => <Svg {...p}><path d="M4.5 12a7.5 7.5 0 1 0 2.6-5.7" /><path d="M4.5 5.5V11h5" /></Svg>,
  Help: (p: { size?: number }) => <Svg {...p}><circle cx="12" cy="12" r="8.5" /><path d="M9.7 9.6a2.4 2.4 0 1 1 2.9 2.6v1.2" /><path d="M12.6 16.6h.01" /></Svg>,
  Back: (p: { size?: number }) => <Svg size={p.size ?? 14} stroke={2}><path d="M19 12H5.5" /><path d="m11 5.5-6 6.5 6 6.5" /></Svg>,
  Check: (p: { size?: number }) => <Svg size={p.size ?? 12} stroke={3}><path d="m5 12.5 4.5 4.5L19 7" /></Svg>,
  Chevron: (p: { size?: number }) => <Svg size={p.size ?? 16} stroke={1.9}><path d="m6 9.5 6 6 6-6" /></Svg>,
  Info: (p: { size?: number }) => <Svg size={p.size ?? 14} stroke={1.9}><circle cx="12" cy="12" r="8.5" /><path d="M12 8v4.5M12 16h.01" /></Svg>,
  Clock: (p: { size?: number }) => <Svg size={p.size ?? 15} stroke={1.9}><circle cx="12" cy="12" r="8" /><path d="M12 8.2V12l2.6 1.6" /></Svg>,
  Close: (p: { size?: number }) => <Svg size={p.size ?? 15} stroke={1.9}><path d="M6 6l12 12M18 6 6 18" /></Svg>,
  Pin: (p: { size?: number }) => <Svg size={p.size ?? 15}><path d="M12 20.5s6.5-5.6 6.5-10.5a6.5 6.5 0 0 0-13 0c0 4.9 6.5 10.5 6.5 10.5Z" /><circle cx="12" cy="10" r="2.3" /></Svg>,
  Apple: ({ size = 15 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M16.4 12.9c0-2.2 1.8-3.3 1.9-3.4-1-1.5-2.6-1.7-3.2-1.7-1.4-.1-2.6.8-3.3.8-.7 0-1.7-.8-2.9-.8-1.5 0-2.9.9-3.7 2.3-1.6 2.7-.4 6.8 1.1 9 .8 1.1 1.7 2.3 2.9 2.2 1.2 0 1.6-.7 3-.7 1.4 0 1.8.7 3 .7 1.2 0 2-1.1 2.8-2.2.6-.9.9-1.7 1-2.1-2.2-.8-2.6-3.1-2.6-4.1ZM14.3 5.9c.6-.8 1.1-1.9 1-3-.9.1-2 .6-2.7 1.4-.6.7-1.1 1.8-1 2.9 1 0 2-.5 2.7-1.3Z" />
    </svg>
  ),
  Google: ({ size = 15 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M21.6 12.2c0-.7-.1-1.3-.2-1.9H12v3.6h5.4c-.2 1.2-1 2.3-2 3v2.5h3.2c1.9-1.7 3-4.3 3-7.2Z" />
      <path fill="#34A853" d="M12 22c2.7 0 4.9-.9 6.6-2.4l-3.2-2.5c-.9.6-2 1-3.4 1-2.6 0-4.8-1.8-5.6-4.1H3.1v2.6C4.8 20 8.1 22 12 22Z" />
      <path fill="#FBBC05" d="M6.4 14c-.2-.6-.3-1.3-.3-2s.1-1.4.3-2V7.4H3.1A10 10 0 0 0 2 12c0 1.6.4 3.2 1.1 4.6L6.4 14Z" />
      <path fill="#EA4335" d="M12 5.9c1.5 0 2.8.5 3.8 1.5l2.8-2.8C16.9 2.9 14.7 2 12 2 8.1 2 4.8 4 3.1 7.4L6.4 10c.8-2.3 3-4.1 5.6-4.1Z" />
    </svg>
  ),
};

export function initialsOf(first?: string | null, last?: string | null): string {
  return `${(first || '')[0] || ''}${(last || '')[0] || ''}`.toUpperCase() || '?';
}

/** Shared mobile flag so callers do not each import the hook path. */
export { useIsMobile };
